/**
 * Shared bottom-anchored sheet modal.
 *
 * Wraps React Native's `<Modal>` with:
 *   - Translucent backdrop (`rgba(0,0,0,0.5)`) that fills the screen
 *     and anchors the card to the bottom (`justifyContent: 'flex-end'`).
 *   - A rounded-top-corner card with the theme's `card` background.
 *   - **Safe-area-aware bottom padding**, derived from `useSafeAreaInsets()`
 *     with a configurable floor — so the card's interactive elements
 *     never fall under Android's system gesture / nav-bar zone.
 *
 * **Why this exists.** Run 2026-06-01T11-04-27-091Z surfaced a production
 * bug on Medium_Phone_API_36.1 (1080×2400): the `AIConsentModal`'s
 * Decline button rendered at y=2307 — 93px from the bottom of the
 * screen, inside Android's gesture-nav zone. The test runner's tap at
 * those coordinates was intercepted by the OS as a HOME gesture (logcat
 * confirmed `LAUNCHER_TASKBAR_HOME_BUTTON_TAP`), backgrounding the app
 * and cascading 10 subsequent test failures. Real Android users on
 * gesture-nav phones hit the same issue: tapping Decline bounces them
 * to the home screen.
 *
 * iOS doesn't reproduce it because RN's `Modal` applies safe-area
 * insets automatically on iOS. Android needs explicit handling.
 *
 * Use this for any bottom-anchored sheet (consent prompts, pickers,
 * confirmations) so the safe-area handling is consistent and the
 * gesture-area overlap bug can't sneak back in per-modal.
 *
 * **API**: pass the sheet's *content* as children — the component
 * provides the Modal wrapper, backdrop, card chrome, and safe-area
 * padding. Children typically wrap their content in a `ScrollView`
 * since sheets often hold more than fits on small screens; the
 * caller controls the scroll behavior.
 *
 * **Storybook**: pass `embedded` to render the card inline without
 * the Modal/backdrop wrapper.
 */
import { ReactNode } from "react";
import { Modal, type DimensionValue } from "react-native";
import { Box } from "@gluestack-ui/themed";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useThemeColors } from "../../theme";

interface BottomSheetModalProps {
  /** Visibility of the sheet. Ignored when `embedded` is true. */
  visible?: boolean;
  /** Fired when the user requests dismissal (Android back button, etc.). */
  onRequestClose?: () => void;
  /** Render the card inline without the Modal/backdrop wrapper — for Storybook. */
  embedded?: boolean;
  /**
   * Max card height as a percentage of the screen, e.g. `'85%'`. Default
   * `'85%'`. Pass a number to use an absolute pixel cap instead. Set to
   * `'100%'` for a full-height sheet.
   */
  maxHeight?: DimensionValue;
  /**
   * Minimum bottom padding inside the card, applied via
   * `paddingBottom: max(insets.bottom, minBottomPadding)`. Default 20.
   * The floor matters when `insets.bottom` is 0 (e.g. iOS Modal on a
   * device with no home indicator) — we still want visual breathing
   * room below the last interactive element.
   */
  minBottomPadding?: number;
  /** Test ID applied to the card (useful for E2E queries). */
  testID?: string;
  children: ReactNode;
}

export function BottomSheetModal({
  visible = true,
  onRequestClose,
  embedded = false,
  maxHeight = "85%",
  minBottomPadding = 20,
  testID,
  children,
}: BottomSheetModalProps) {
  const theme = useThemeColors();
  const insets = useSafeAreaInsets();
  // Floor at minBottomPadding so we always have visual breathing room
  // beneath the card's last interactive element, even when the system
  // reports a 0 bottom inset.
  const paddingBottom = Math.max(insets.bottom, minBottomPadding);

  const card = (
    <Box
      testID={testID}
      style={{
        backgroundColor: theme.card,
        borderTopLeftRadius: embedded ? undefined : 20,
        borderTopRightRadius: embedded ? undefined : 20,
        borderRadius: embedded ? 16 : undefined,
        maxHeight: embedded ? undefined : maxHeight,
        paddingTop: 20,
        paddingHorizontal: 20,
        // Safe-area-aware bottom padding — this is the line that
        // prevents the Decline-button-on-Android-gesture-zone bug.
        paddingBottom,
      }}
    >
      {children}
    </Box>
  );

  if (embedded) return card;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onRequestClose}
    >
      <Box
        flex={1}
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        justifyContent="flex-end"
      >
        {card}
      </Box>
    </Modal>
  );
}
